import React from 'react';
import {
    ActivityEntityName,
    RoleId,
    useCurrentUserQuery,
} from '../../../services';
import { IRhTab } from '../../dum';
import { EntityHistorial } from './entity-historial';

interface IUseHistorialTabArgs {
    entityName: ActivityEntityName;
    entityId?: number | null;
    /**
     * Shown before the record exists. Passed in rather than derived from
     * entityName because Spanish gender and noun differ per entity ("la venta
     * ha sido guardada" vs "el pedido ha sido guardado").
     */
    unsavedMessage: string;
    emptyMessage: string;
}

/**
 * Builds the `Historial` tab for an upsert form, or an empty array for anyone
 * who cannot read the audit.
 *
 * Returns an array so the caller can spread it straight into `tabs={[...]}`:
 * a reader without audit access then gets no tab at all rather than an empty
 * one. Every form that exposes audit history goes through here, so the role
 * check, the tab label and the read-only tab shape are defined once and cannot
 * drift across the ten forms that use it.
 *
 * Audit reading is for the three GLOBAL roles — Super, General and Asistente
 * General. Asistente General is globally read-only: it already reads the live
 * records through the ordinary queries, and the audit adds their historical
 * values. It gains nothing else here — this tab has no mutation of any kind.
 *
 * The check is a convenience only. getEntityActivities and getActivity are gated
 * with RoleId.ADMIN on the server, which GqlRolesGuard admits those same three
 * roles to on a query; hiding the tab just avoids a dead click.
 */
export function useHistorialTab({
    entityName,
    entityId,
    unsavedMessage,
    emptyMessage,
}: IUseHistorialTabArgs): IRhTab[] {
    // Called unconditionally — the role check narrows the RESULT, never whether
    // the hook runs.
    const { data: currentUserData } = useCurrentUserQuery();

    const canReadAudit =
        !!currentUserData &&
        [RoleId.Super, RoleId.Admin, RoleId.Guest].some((roleId) =>
            currentUserData.currentUser.role_ids.includes(roleId),
        );

    if (!canReadAudit) return [];

    return [
        {
            label: 'Historial',
            // Read-only audit view: no form fields, so no progress counter.
            optional: true,
            fields: [],
            content: (
                <EntityHistorial
                    entityName={entityName}
                    entityId={entityId}
                    unsavedMessage={unsavedMessage}
                    emptyMessage={emptyMessage}
                />
            ),
        },
    ];
}
